/**
 * Bodies recovered from the outbox that the server has not taken yet.
 *
 * Plan: `docs/plans/2026-09-08-next-steps-annotation-write-volume.md` §5 phase 3
 * ("overlay, then drain"). Spec: the companion `-implementation-spec.md` §5.
 *
 * WHY THIS EXISTS. The launch overlay used to be a one-shot patch into Vuex,
 * run right after the keys were ready. For an enrolled member that is too
 * early to patch anything: their plaintext caches are off, so `allNotes` is
 * empty at launch and the Hub only fills it when it opens, page by page from
 * the corpus. The patch landed on nothing, and the Hub's own `setAllNotes`
 * would have replaced it anyway. So the overlay is a RULE the read path
 * honours, exactly as item 1f is for the live queue: this registry says which
 * rows still have an unsent body on disk, and the fetch mutations apply that
 * body over whatever the server returns for the row.
 *
 * Plaintext, in memory only, for the life of the process — the same standing
 * as the Vuex state it is applied to. The durable copy stays sealed in the
 * outbox; this is only the opened form of it, so a fetch can be answered
 * synchronously (a mutation cannot await Preferences).
 *
 * An entry leaves here the moment the row is no longer the outbox's to send:
 * the drain sent it, dropped it, or turned it into a second note; or the live
 * engine took the key over (the member typed on the row), after which the
 * engine's own send clears it on success.
 */
const patches = new Map();

export const pendingOverlay = {
  /** @param {string} key @param {object} patch the opened fields to lay over a fetched row */
  set(key, patch) {
    if (key && patch) patches.set(key, patch);
  },
  has: key => patches.has(key),
  /** @returns {object|null} */
  patchFor: key => patches.get(key) ?? null,
  clear(key) {
    patches.delete(key);
  },
  keys: () => [...patches.keys()],
  /** Specs and teardown. */
  reset() {
    patches.clear();
  },
};
